/* eslint-disable jsx-a11y/aria-role */
import React, { useMemo, useState } from 'react';
import ProfileImageModal from '@medflex/components/stories/ProfileImageModal';
import { useTranslation } from 'react-i18next';
import { showSuccessToast, showAlertToast } from '@medflex/components/stories/Toast';
import Avatar from '@medflex/components/stories/Avatar';
import { TRACKING_ACTIONS, trackEvent } from '@medflex/utils/Tracking';
import useUppy from '@medflex/hooks/useUppy';
import config from '@medflex/utils/config';
import useUserSession from '@medflex/hooks/useUserSession';
import isWebView from '@medflex/utils/isWebView';
import useUserProfile from '../hooks/useUserProfile';
import useModal from '../hooks/useModal';
import { callFlutterHandler, FLUTTER_CALLBACKS } from '../../utils/mobile';

const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/tiff'];

const ProfileImageModule = () => {
  const { userProfile, update } = useUserProfile();
  const { session } = useUserSession();
  const { avatar, initials } = useMemo(() => userProfile.attributes, [userProfile.attributes]);
  const { close } = useModal();
  const [saving, setSaving] = useState(false);
  const { t } = useTranslation('physician');

  const handleError = () => {
    showAlertToast(
      t('toasts.profile_image_update.failure.title'),
      t('toasts.profile_image_update.failure.text'),
      { toastId: 'profileImageFailed' },
    );
    trackEvent({
      zone: 'Profile_Image',
      action: TRACKING_ACTIONS.SUBMIT,
      actionText: 'update failure',
    });
  };

  const { upload, addBlob, reset } = useUppy({
    endpoint: `${config.PROFILE_SERVICE_ENDPOINT}/uploads`,
    headers: {
      AUTHORIZATION: `Bearer ${session.access_token || ''}`,
    },
    restrictions: {
      allowedFileTypes: ALLOWED_FILE_TYPES,
      maxTotalFileSize: 80 * 1024 * 1024,
    },
    onRestrictionFailed: handleError,
  });

  const handleChange = async ({ avatar: newAvatar }) => {
    try {
      setSaving(true);
      addBlob(newAvatar);
      const { successful } = await upload();
      const [file] = successful;
      const [id] = file.uploadURL.split('/').slice(-1);
      const url = `${config.PUBLIC_PROFILE_SERVICE_ENDPOINT}/avatar/${id}`;
      await update({ avatar: url });
      trackEvent({
        zone: 'Profile_Image',
        action: TRACKING_ACTIONS.SUBMIT,
        actionText: 'update success',
      });
      showSuccessToast(t('toasts.profile_image_update.success'), null, {
        toastId: 'profileImageUpdateSuccess',
      });
      reset();
      // web part of the fix for https://medflexgmbh.atlassian.net/browse/MMA-463
      if (isWebView()) {
        callFlutterHandler(FLUTTER_CALLBACKS.UPDATE_PROFILE_IMAGE);
      }
    } catch (e) {
      handleError(e);
    }
    setSaving(false);
  };

  const handleClose = () => {
    close({ modal: 'editProfileImage' });
  };

  return (
    <ProfileImageModal
      apiKey="SOME_NEW_API_KEY"
      onChange={handleChange}
      onClose={handleClose}
      loading={saving}
      allowedFileTypes={ALLOWED_FILE_TYPES}
    >
      <Avatar
        src={avatar}
        resizeDimension="400x"
        initials={initials}
        size="none"
        className="mb-4 w-full"
        style={{ maxWidth: '400px', maxHeight: '400px' }}
      />
    </ProfileImageModal>
  );
};

export default ProfileImageModule;
